/**
 * WP-V2-IO — kontrakt interaktywnych obiektów (PLAN-V2 §3.1).
 * Sceny tylko KONSUMUJĄ ten moduł; nie importują avatar/ bezpośrednio.
 */

/** Rodzaje interakcji obsługiwane przez silnik. */
export type InteractionKind =
  | 'tap'
  | 'drag'
  | 'pickup'
  | 'give'
  | 'consume'
  | 'toggle'
  | 'bounce';

/** Proceduralne FX (zero assetów PNG). */
export type ObjectFxKind =
  | 'hearts'
  | 'stars'
  | 'crumbs'
  | 'splash'
  | 'notes'
  | 'sparkle';

/** Reakcja psa po użyciu obiektu (mapowana na playAction). */
export type DogReaction = 'eat' | 'happy' | 'jump' | 'sniff';

/** Definicja typu obiektu (katalog sceny). */
export type ObjectDef = {
  /** Unikalny id typu, np. 'cafe_cupcake'. */
  id: string;
  /** Klucz tekstury Phaser; brak → kolorowy kształt + ikona. */
  texture: string;
  /** Rozmiar docelowy w px; cel dotykowy ≥64. */
  size: number;
  interactions: InteractionKind[];
  /** Stany, np. ['full','empty'] → tekstury `obj_<id>_<state>` lub fallback. */
  states?: string[];
  sound?: string;
  fx?: ObjectFxKind;
  dogReaction?: DogReaction;
  /** Id stref snap, do których drag się przyciąga. */
  snapTargets?: string[];
};

/** Rozmieszczenie instancji na scenie. */
export type SpawnPlacement = {
  defId: string;
  x: number;
  y: number;
};

/**
 * Luźny cel psa/NPC — NIE importujemy avatar/.
 * Sceny podają AnimatedDogAvatar / mock spełniający ten kształt.
 */
export type InteractTarget = {
  moveTo(x: number, y: number): void;
  holdItem(textureKey: string | null): void;
  playAction(
    a: 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy' | 'sniff' | string,
  ): Promise<void> | void;
  /** Pozycja świata (preferowane). */
  x?: number;
  y?: number;
  getPosition?: () => { x: number; y: number };
  /** true gdy moveTo jeszcze w toku (opcjonalne). */
  isMoving?: boolean;
};

/** Strefa przyciągania drag&drop (miska, stół, talerz). */
export type SnapZone = {
  id: string;
  x: number;
  y: number;
  /** Promień w px; domyślnie 48 w warstwie. */
  radius: number;
};

export type InteractEventType =
  | 'tap'
  | 'drag-start'
  | 'drag-move'
  | 'drag-end'
  | 'pickup'
  | 'drop'
  | 'give'
  | 'consume'
  | 'toggle'
  | 'bounce'
  | 'snap'
  | 'intention-enqueued'
  | 'intention-approach'
  | 'intention-done'
  | 'intention-cancelled';

export type InteractEvent = {
  type: InteractEventType;
  /** Id instancji (unikalne na warstwie). */
  objectId: string;
  /** Id definicji (ObjectDef.id). */
  defId: string;
  x: number;
  y: number;
  state?: string;
  snapZoneId?: string;
  kind?: InteractionKind;
  payload?: unknown;
};

export type InteractListener = (evt: InteractEvent) => void;

/** Minimalny rozmiar celu dotykowego (PLAN §3 / UiKit.MIN_TOUCH). */
export const MIN_OBJECT_SIZE = 64;

/** Mnożnik hit-area względem sprite'a (małe łapki). */
export const HIT_AREA_SCALE = 1.3;

/** Domyślny zasięg podejścia psa do obiektu (px). */
export const DEFAULT_INTERACT_RANGE = 90;
